import { BulkUploadDataValidationInterfaceInstanceRequest, bulkUploadPpsResponseInterface, ErrorDescriptionInterface } from 'Interfaces/JobsInterface';
import { useStore } from 'hooks/useStore';
import { Nullable } from 'primereact/ts-helpers';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { JobPasteServices } from 'services/jobPasteServices';
import { deepCopy } from 'utils/deepCopy';
import { formatJobStartTime } from 'utils/jobStartTimeUtilities';
import { v4 as uuid } from 'uuid';

interface OptionsInterface {
  code: string;
  name: string;
}

const useDataValidationComponent = ({
  data,
  successCallBack
}: {
  data: BulkUploadDataValidationInterfaceInstanceRequest[];
  successCallBack: () => void;
}) => {
  const [bulkUpdateData, setBulkUpdateData] = useState<BulkUploadDataValidationInterfaceInstanceRequest[]>(
    data.map(item => {
      return {
        ...item,
        status: item.status ? item.status?.toLowerCase() : null,
        runType: item.runType.toLowerCase(),
        room: item.room.toUpperCase(),
        id: uuid()
      };
    })
  );
  const [errorDescriptions, setErrorDescriptions] = useState<ErrorDescriptionInterface[]>([]);
  const [view, setView] = useState<'view' | 'edit'>('view');

  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const [isAnimationRunning, setIsAnimationRunning] = useState(false);
  const [bulkUpdateResponse, setBulkUpdateResponse] =
    useState<bulkUploadPpsResponseInterface | null>(null);
  const [storedJobDetailsRowIndex, setStoredJobDetailsRowIndex] = useState<null | number>(null);

  const { JobPasteStore } = useStore();

  // Define options for dropdown selectors
  const priorityOptions: OptionsInterface[] = [
    { code: '1', name: '1' },
    { code: '3', name: '3' }
  ];

  const statusOptions: OptionsInterface[] = [{ code: 'ready', name: 'ready' }];

  const runTypeOptions: OptionsInterface[] = [
    { code: 'run', name: 'Run' },
    { code: 'dump', name: 'Dump' },
    { code: 'overlay', name: 'Overlay' },
    { code: 'recap', name: 'Recap' }
  ];

  const roomOptions = [
    { name: 'Purple', code: 'PU' },
    { name: 'Green', code: 'GR' },
    { name: 'Gold', code: 'GO' },
    { name: 'Blue', code: 'BL' },
    { name: 'Black', code: 'BK' },
    { name: 'Silver', code: 'SI' },
    { name: 'RedC', code: 'RC' },
    { name: 'RedD', code: 'RD' },
    { name: 'WhiteA', code: 'WA' },
    { name: 'WhiteB', code: 'WB' },
    { name: 'RedB', code: 'RB' }
  ];

  // Helper functions for getting selected options for rows
  const getPrioritySelectedOptionForRow = (rowIndex: number) => {
    let selectedOption: OptionsInterface | null = null;
    for (const option of priorityOptions) {
      if (option.code === bulkUpdateData[rowIndex].priority) {
        selectedOption = option;
      }
    }
    return selectedOption;
  };

  const getStatusSelectedOptionForRow = (rowIndex: number) => {
    let selectedOption: OptionsInterface | null = null;
    for (const option of statusOptions) {
      if (option.code === bulkUpdateData[rowIndex].status) {
        selectedOption = option;
      }
    }
    return selectedOption;
  };

  const getRunTypeSelectedOptionForRow = (rowIndex: number) => {
    let selectedOption: OptionsInterface | null = null;
    for (const option of runTypeOptions) {
      if (option.code === bulkUpdateData[rowIndex].runType) {
        selectedOption = option;
      }
    }
    return selectedOption;
  };

  const getRoomselectedOptionForRow = (rowIndex: number) => {
    let selectedOption: OptionsInterface | null = null;
    for (const option of roomOptions) {
      if (option.code === bulkUpdateData[rowIndex].room) {
        selectedOption = option;
      }
    }
    return selectedOption;
  };

  const handleVerify = async () => {
    try {
      setIsBulkUpdating(true);

      const transformedBulkJobData = bulkUpdateData.map(job => {
        return {
          ...job,
          confirmedDate: new Date(job.confirmedDate),
          jobStartTime: job.jobStartTime ? formatJobStartTime(job.jobStartTime) : job.jobStartTime
        };
      });
      const bulkUpdateResponse = await JobPasteServices.bulkUpdateJobs(
        transformedBulkJobData as any
      );
      setBulkUpdateResponse(bulkUpdateResponse);

      if (bulkUpdateResponse === 'Success') {
        toast.success('Successfully Bulk Uploaded Jobs');
        
        // Backend handles WebSocket broadcasting automatically
        // No need to send WebSocket message from frontend
        // WebSocket will automatically update the jobs list
        
        successCallBack();
        setErrorDescriptions([]);
        setView('view');
        return;
      }

      if (
        bulkUpdateResponse.isfileStructurePpsJobsValidity.invalid ||
        bulkUpdateResponse.isFileDatesOverlapping.invalid ||
        bulkUpdateResponse.isJobIdsUnique.invalid ||
        bulkUpdateResponse.isNDCFormatValid.invalid
      ) {
        toast.error('Data validation failed, please resolve errors');
        setIsAnimationRunning(true);

        if (bulkUpdateResponse.isfileStructurePpsJobsValidity.invalid) {
          setErrorDescriptions(
            bulkUpdateResponse.isfileStructurePpsJobsValidity.error_descriptions
          );
          setTimeout(() => {
            setIsAnimationRunning(false);
          }, 6000);
        } else if (bulkUpdateResponse.isFileDatesOverlapping.invalid) {
          setErrorDescriptions(bulkUpdateResponse.isFileDatesOverlapping.error_descriptions);
          setTimeout(() => {
            setIsAnimationRunning(false);
          }, 6000);
        } else if (bulkUpdateResponse.isJobIdsUnique.invalid) {
          setErrorDescriptions(bulkUpdateResponse.isJobIdsUnique.error_descriptions);
          setTimeout(() => {
            setIsAnimationRunning(false);
          }, 6000);
        } else if (bulkUpdateResponse.isNDCFormatValid.invalid) {
          setErrorDescriptions(bulkUpdateResponse.isNDCFormatValid.error_descriptions);
          setTimeout(() => {
            setIsAnimationRunning(false);
          }, 6000);
        }

        setView('edit');
      } else {
        toast.success('Uploaded Successfully!');
        successCallBack();
      }
    } catch {
      toast.error('Failed to bulk update jobs');
    } finally {
      setIsBulkUpdating(false);
    }
  };

  const checkIfThereAreErrors = () => {
    return errorDescriptions.length > 0;
  };

  function isNumeric(str: string) {
    if (typeof str !== 'string') {
      return false;
    } // we only process strings!
    return (
      !isNaN(+str) && // use type coercion to parse the _entirety_ of the string (`parseFloat` alone does not do this)...
      !isNaN(parseFloat(str))
    ); // ...and ensure strings of whitespace fail
  }

  // Form update methods to handle changes in each field
  const formUpdateMethods = {
    handleUpdateJobId: (jobId: string, rowIndex: number) => {
      const tempBulkUpdateData = deepCopy(bulkUpdateData);
      tempBulkUpdateData[rowIndex].jobId = jobId;
      setBulkUpdateData(tempBulkUpdateData);
    },

    handleUpdatePriority: (priority: string, rowIndex: number) => {
      const tempBulkUpdateData = deepCopy(bulkUpdateData);
      tempBulkUpdateData[rowIndex].priority = priority;
      setBulkUpdateData(tempBulkUpdateData);
    },

    handleUpdateStatus: (status: string, rowIndex: number) => {
      const tempBulkUpdateData = deepCopy(bulkUpdateData);
      tempBulkUpdateData[rowIndex].status = status;
      setBulkUpdateData(tempBulkUpdateData);
    },

    handleUpdateRoom: (room: string, rowIndex: number) => {
      const tempBulkUpdateData = deepCopy(bulkUpdateData);
      tempBulkUpdateData[rowIndex].room = room;
      setBulkUpdateData(tempBulkUpdateData);
    },

    handleUpdateNDC: (ndc: string, rowIndex: number) => {
      const tempBulkUpdateData = deepCopy(bulkUpdateData);
      tempBulkUpdateData[rowIndex].ndc = ndc;
      setBulkUpdateData(tempBulkUpdateData);
    },

    handleUpdateDescription: (description: string, rowIndex: number) => {
      const tempBulkUpdateData = deepCopy(bulkUpdateData);
      tempBulkUpdateData[rowIndex].description = description;
      setBulkUpdateData(tempBulkUpdateData);
    },

    handleUpdateQtyContainers: (qty: string, rowIndex: number) => {
      if (isNumeric(qty) || qty === '') {
        const tempBulkUpdateData = deepCopy(bulkUpdateData);
        tempBulkUpdateData[rowIndex].qtyOfContainersToFill = qty;
        setBulkUpdateData(tempBulkUpdateData);
      }
    },

    handleUpdateQtyPerContainer: (qty: string, rowIndex: number) => {
      if (isNumeric(qty) || qty === '') {
        const tempBulkUpdateData = deepCopy(bulkUpdateData);
        tempBulkUpdateData[rowIndex].qtyPerContainer = qty;
        setBulkUpdateData(tempBulkUpdateData);
      }
    },

    handleUpdateRunType: (runType: string, rowIndex: number) => {
      const tempBulkUpdateData = deepCopy(bulkUpdateData);
      tempBulkUpdateData[rowIndex].runType = runType;
      setBulkUpdateData(tempBulkUpdateData);
    },

    handleUpdateQtyUnitsToPackage: (qty: string, rowIndex: number) => {
      if (isNumeric(qty) || qty === '') {
        const tempBulkUpdateData = deepCopy(bulkUpdateData);
        tempBulkUpdateData[rowIndex].qtyUnitsToPackage = qty;
        setBulkUpdateData(tempBulkUpdateData);
      }
    },

    handleUpdateConfirmedDate: (date: Date, rowIndex: number) => {
      const tempBulkUpdateData = deepCopy(bulkUpdateData);
      // Format the date as mm/dd/yyyy
      const month = (date.getMonth() + 1).toString().padStart(2, '0'); // getMonth() is 0-indexed
      const day = date.getDate().toString().padStart(2, '0');
      const year = date.getFullYear();
      tempBulkUpdateData[rowIndex].confirmedDate = date; // Store as Date object
      setBulkUpdateData(tempBulkUpdateData);
    },

    handleUpdateInitialNote: (note: string, rowIndex: number) => {
      const tempBulkUpdateData = deepCopy(bulkUpdateData);
      tempBulkUpdateData[rowIndex].initialNote = note;
      setBulkUpdateData(tempBulkUpdateData);
    },

    handleUpdateNumberOfBottles: (bottles: string, rowIndex: number) => {
      if (isNumeric(bottles) || bottles === '') {
        const tempBulkUpdateData = deepCopy(bulkUpdateData);
        tempBulkUpdateData[rowIndex].numberOfBottlesAtShiftStart = bottles;
        setBulkUpdateData(tempBulkUpdateData);
      }
    },

    handleUpdateJobStartTime: (time: string, rowIndex: number) => {
      const tempBulkUpdateData = deepCopy(bulkUpdateData);
      // Convert string time to Date object if needed
      tempBulkUpdateData[rowIndex].jobStartTime = time ? new Date(time) : null;
      setBulkUpdateData(tempBulkUpdateData);
    }
  };

  // Helper function for date formatting
  // const formatDate = (date: Date): string => {
  //   return date.toISOString();
  // };

  // Parse date string to Date object
  const parseDate = (dateString: string): Date | null => {
    if (!dateString) {
      return null;
    }
    return new Date(dateString);
  };

  // Define all input operations for component
  const inputOperations = {
    jobId: {
      value: (rowIndex: number) => {
        return bulkUpdateData[rowIndex].jobId;
      },
      onChange: (rowIndex: number, value: string) => {
        formUpdateMethods.handleUpdateJobId(value, rowIndex);
      }
    },
    priority: {
      value: (rowIndex: number) => {
        return getPrioritySelectedOptionForRow(rowIndex);
      },
      onChange: (rowIndex: number, value: string) => {
        formUpdateMethods.handleUpdatePriority(value, rowIndex);
      }
    },
    status: {
      value: (rowIndex: number) => {
        return getStatusSelectedOptionForRow(rowIndex);
      },
      onChange: (rowIndex: number, value: string) => {
        formUpdateMethods.handleUpdateStatus(value, rowIndex);
      }
    },
    room: {
      value: (rowIndex: number) => {
        return getRoomselectedOptionForRow(rowIndex);
      },
      onChange: (rowIndex: number, value: string) => {
        formUpdateMethods.handleUpdateRoom(value, rowIndex);
      }
    },
    ndc: {
      value: (rowIndex: number) => {
        return bulkUpdateData[rowIndex].ndc;
      },
      onChange: (rowIndex: number, value: string) => {
        formUpdateMethods.handleUpdateNDC(value, rowIndex);
      }
    },
    description: {
      value: (rowIndex: number) => {
        return bulkUpdateData[rowIndex].description;
      },
      onChange: (rowIndex: number, value: string) => {
        formUpdateMethods.handleUpdateDescription(value, rowIndex);
      }
    },
    qtyOfContainersToFill: {
      value: (rowIndex: number) => {
        return bulkUpdateData[rowIndex].qtyOfContainersToFill;
      },
      onChange: (rowIndex: number, value: string) => {
        formUpdateMethods.handleUpdateQtyContainers(value, rowIndex);
      }
    },
    qtyPerContainer: {
      value: (rowIndex: number) => {
        return bulkUpdateData[rowIndex].qtyPerContainer;
      },
      onChange: (rowIndex: number, value: string) => {
        formUpdateMethods.handleUpdateQtyPerContainer(value, rowIndex);
      }
    },
    runType: {
      value: (rowIndex: number) => {
        return getRunTypeSelectedOptionForRow(rowIndex);
      },
      onChange: (rowIndex: number, value: string) => {
        formUpdateMethods.handleUpdateRunType(value, rowIndex);
      }
    },
    qtyUnitsToPackage: {
      value: (rowIndex: number) => {
        return bulkUpdateData[rowIndex].qtyUnitsToPackage;
      },
      onChange: (rowIndex: number, value: string) => {
        formUpdateMethods.handleUpdateQtyUnitsToPackage(value, rowIndex);
      }
    },
    confirmedDate: {
      value: (rowIndex: number) => {
        const dateString = bulkUpdateData[rowIndex].confirmedDate;
        return dateString ? new Date(dateString) : null;
      },
      onChange: (value: Nullable<Date>, rowIndex: number) => {
        if (value) {
          formUpdateMethods.handleUpdateConfirmedDate(value, rowIndex);
        }
      }
    },
    initialNote: {
      value: (rowIndex: number) => {
        return bulkUpdateData[rowIndex].initialNote || '';
      },
      onChange: (rowIndex: number, value: string) => {
        formUpdateMethods.handleUpdateInitialNote(value, rowIndex);
      }
    },
    numberOfBottlesAtShiftStart: {
      value: (rowIndex: number) => {
        return bulkUpdateData[rowIndex].numberOfBottlesAtShiftStart || '';
      },
      onChange: (rowIndex: number, value: string) => {
        formUpdateMethods.handleUpdateNumberOfBottles(value, rowIndex);
      }
    },
    jobStartTime: {
      value: (rowIndex: number) => {
        const timeString = bulkUpdateData[rowIndex].jobStartTime;
        return timeString || null;
      },
      onChange: (rowIndex: number, value: string) => {
        formUpdateMethods.handleUpdateJobStartTime(value, rowIndex);
      }
    }
  };

  const handleRowDeleteButtonClick = (rowIndex: number) => {
    // delete the row from the bulkUpdateData
    if (rowIndex < bulkUpdateData.length) {
      const tempBulkUdateData = deepCopy(bulkUpdateData);
      tempBulkUdateData.splice(rowIndex, 1);
      setBulkUpdateData(tempBulkUdateData);
    }

    // fix the entries in the error description array
    const tempNewArrayDescription: ErrorDescriptionInterface[] = [];

    for (let i = 0; i < errorDescriptions.length; i++) {
      if (errorDescriptions[i].row < rowIndex) {
        tempNewArrayDescription.push(errorDescriptions[i]);
      } else if (errorDescriptions[i].row > rowIndex) {
        const editedErrorDescription = deepCopy(errorDescriptions[i]);
        editedErrorDescription.row = editedErrorDescription.row - 1;
        tempNewArrayDescription.push(editedErrorDescription);
      }
    }

    setErrorDescriptions(tempNewArrayDescription);
  };

  const getErrorDescription = (rowIndex: number) => {
    for (let i = 0; i < errorDescriptions.length; i++) {
      if (errorDescriptions[i].row === rowIndex) {
        return errorDescriptions[i].Error;
      }
    }
    return '';
  };

  const getMaxWidthForProgressBar = () => {
    if (bulkUpdateResponse && bulkUpdateResponse !== 'Success') {
      if (bulkUpdateResponse.isfileStructurePpsJobsValidity.invalid) {
        return '25% !important';
      } else if (bulkUpdateResponse.isFileDatesOverlapping.invalid) {
        return '50% !important';
      } else if (bulkUpdateResponse.isJobIdsUnique.invalid) {
        return '75% !important';
      } else if (bulkUpdateResponse.isNDCFormatValid.invalid) {
        return '90% !important';
      }
    }
    return '100% !important';
  };

  const checkIfVerifyDataButtonIsDiabled = () => {
    return bulkUpdateData.length ? false : true;
  };

  const handleCloseJobDetailsModal = () => {
    setStoredJobDetailsRowIndex(null);
  };

  const handleClickJobDetails = (rowIndex: number) => {
    setStoredJobDetailsRowIndex(rowIndex);
  };

  return {
    bulkUpdateData,
    view,
    isBulkUpdating,
    checkIfThereAreErrors,
    errorDescriptions,
    handleVerify,
    handleRowDeleteButtonClick,
    getErrorDescription,
    isAnimationRunning,
    bulkUpdateResponse,
    getMaxWidthForProgressBar,
    priorityOptions,
    statusOptions,
    runTypeOptions,
    getPrioritySelectedOptionForRow,
    getStatusSelectedOptionForRow,
    getRunTypeSelectedOptionForRow,
    inputOperations,
    checkIfVerifyDataButtonIsDiabled,
    storedJobDetailsRowIndex,
    handleCloseJobDetailsModal,
    handleClickJobDetails,
    roomOptions
  };
};

export default useDataValidationComponent;
